import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { API, th, td, inp, StatPill } from "./shared";
import { GstFixModal, CreditNotesPanel } from "./CreditNotes";
import { MarginReport } from "./MarginReport";

function ZohoBadge({ inv, onDone }) {
  const [busy, setBusy] = useState(false);
  const push = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/invoices/${inv.id}/zoho-sync`);
      data.zoho_status === "synced" ? toast.success(`Synced to Zoho as ${data.zoho_number}`) : toast.error(data.zoho_error || "Zoho sync failed");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Zoho sync failed"); } finally { setBusy(false); }
  };
  if (inv.zoho_status === "synced") return <span data-testid={`inv-zoho-ok-${inv.id}`} title={`Zoho ${inv.zoho_number}`} style={{fontSize:10,fontWeight:700,color:"#065F46",background:"#ECFDF5",border:"1px solid #A7F3D0",borderRadius:999,padding:"2px 8px",marginLeft:6}}>Zoho ✓</span>;
  return (
    <button data-testid={`inv-zoho-push-${inv.id}`} className="secondary-btn" disabled={busy} onClick={push} title={inv.zoho_error || "Not in Zoho yet"}
      style={{fontSize:11,padding:"4px 10px",marginLeft:6,color: inv.zoho_status === "failed" ? "#B91C1C" : undefined}}>
      {busy ? "Pushing…" : inv.zoho_status === "failed" ? "Retry Zoho" : "Push to Zoho"}
    </button>
  );
}

// =========================================================================
// USER INVOICES — dedicated page for buyers + suppliers
// =========================================================================
export function UserInvoices({ userId, role }) {
  const [data, setData] = useState(null);
  const [fy, setFy] = useState("");
  const [kind, setKind] = useState("");
  const [busy, setBusy] = useState("");
  const [fixing, setFixing] = useState(null);
  const [cnRefresh, setCnRefresh] = useState(0);
  const [gstMonth, setGstMonth] = useState(() => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }).slice(0, 7));

  const load = async () => {
    if (!userId) return;
    const params = new URLSearchParams();
    if (fy) params.set("fy", fy);
    if (kind) params.set("kind", kind);
    const qs = params.toString();
    try {
      const { data } = await axios.get(`${API}/users/${userId}/invoices${qs ? `?${qs}` : ""}`);
      setData(data);
    } catch {
      setData({ invoices: [], totals: {} });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId, fy, kind]);

  const reissue = async (id) => {
    setBusy(id || "all");
    try {
      const { data: r } = await axios.post(`${API}/admin/invoices/${id ? `${id}/reissue` : "reissue-all"}`);
      toast.success(id ? "Invoice reissued with current company details" : `${r.reissued} invoice${r.reissued === 1 ? "" : "s"} reissued`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reissue failed");
    } finally {
      setBusy("");
    }
  };

  const downloadGstr1 = async () => {
    setBusy("gstr1");
    try {
      const r = await axios.get(`${API}/admin/reports/gstr1`, { params: { month: gstMonth }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `GSTR1_${gstMonth}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      const msg = e.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()).detail : null;
      toast.error(msg || "Could not build the GSTR-1 report");
    } finally {
      setBusy("");
    }
  };

  if (!data) return <div className="content" data-testid="user-invoices-loading">Loading…</div>;
  const isAdmin = role === "admin";
  const outdated = data.totals?.outdated_all || 0;
  const t = data.totals || {};
  const isSupplier = ["printer", "agency", "material_supplier", "finishing_partner"].includes(role);

  const zipHref = (() => {
    const params = new URLSearchParams();
    if (fy) params.set("fy", fy);
    if (kind) params.set("kind", kind);
    const qs = params.toString();
    return `${API}/users/${userId}/invoices.zip${qs ? `?${qs}` : ""}`;
  })();

  // Build FY options from the current invoices
  const fyOptions = Array.from(new Set((data.invoices || []).map(i => {
    const d = new Date(i.invoice_date);
    const y = d.getUTCMonth() >= 3 ? d.getUTCFullYear() + 1 : d.getUTCFullYear();
    return `FY${y}`;
  }))).sort().reverse();

  return (
    <section className="content" data-testid="user-invoices">
      <div className="section-heading">
        <div>
          <p className="eyebrow">{isAdmin ? "PLATFORM INVOICES" : isSupplier ? "YOUR SUPPLIER INVOICES" : "YOUR TAX INVOICES"}</p>
          <h2>{isAdmin ? "All platform invoices" : isSupplier ? "Invoices raised on GoPrintPack™" : "All invoices for your orders"}</h2>
          <p className="subhead">
            {isAdmin
              ? "Every supplier → platform and platform → customer tax invoice issued on GoPrintPack™."
              : isSupplier
              ? "Download the invoices you raised on us and our platform-fee tax invoices to you (claim the 18% GST as ITC). TDS u/s 194-O deducted at 1% is also tracked here."
              : "Tax invoices GoPrintPack™ issued to you with full GST breakup. Use these to claim input tax credit (ITC) on your GSTR-2B."}
          </p>
        </div>
        {isAdmin ? (
          <button data-testid="inv-reissue-all" className="primary-btn" disabled={!outdated || !!busy} onClick={() => reissue()}>
            {busy === "all" ? "Reissuing…" : `Reissue all old invoices (${outdated})`}
          </button>
        ) : (
          <a data-testid="inv-download-zip" href={zipHref} className="primary-btn" style={{textDecoration:"none"}}>
            Download all as ZIP
          </a>
        )}
      </div>
      {isAdmin && (
        <p data-testid="inv-reissue-note" style={{fontSize:12,color:"#64748b",margin:"-6px 0 14px"}}>
          Reissue updates only GoPrintPack's address, GSTIN, PAN and state on the invoice. Invoice numbers, dates and amounts stay unchanged.
        </p>
      )}
      {isAdmin && (
        <div data-testid="gstr1-bar" style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:10,padding:"10px 14px",margin:"0 0 16px"}}>
          <b style={{fontSize:13,color:"#1E3A8A"}}>GSTR-1 monthly report</b>
          <input type="month" data-testid="gstr1-month" value={gstMonth} onChange={e => setGstMonth(e.target.value)} style={{padding:"5px 8px",border:"1px solid #CBD5E1",borderRadius:6}}/>
          <button data-testid="gstr1-download" className="primary-btn" disabled={!gstMonth || !!busy} onClick={downloadGstr1}>
            {busy === "gstr1" ? "Building…" : "Download GSTR-1 (.xlsx)"}
          </button>
          <span style={{fontSize:11.5,color:"#475569"}}>Includes the B2B, B2CL, B2CS, HSN (B2B/B2C), Documents and Inward (ITC) sheets, plus a summary</span>
        </div>
      )}
      {isAdmin && <MarginReport/>}

      <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:16}}>
        <StatPill label="Invoices" value={t.count || 0} testid="inv-count"/>
        <StatPill label={isSupplier ? "Taxable (your sales)" : "Taxable value"} value={`₹${(t.taxable_total_inr||0).toLocaleString("en-IN")}`} testid="inv-taxable"/>
        <StatPill label="GST" value={`₹${(t.gst_total_inr||0).toLocaleString("en-IN")}`} testid="inv-gst" tone="ok"/>
        <StatPill label="Grand total" value={`₹${(t.grand_total_inr||0).toLocaleString("en-IN")}`} testid="inv-grand" tone="ok"/>
        {isSupplier && <StatPill label="TDS deducted" value={`₹${(t.tds_total_inr||0).toLocaleString("en-IN")}`} testid="inv-tds" tone="warn"/>}
      </div>

      <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap",alignItems:"center"}}>
        <label style={{fontSize:11,color:"#6b7280"}}>Financial year</label>
        <select data-testid="inv-filter-fy" value={fy} onChange={e => setFy(e.target.value)} style={{...inp,width:140,marginTop:0}}>
          <option value="">All</option>
          {fyOptions.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
        {isSupplier && (
          <>
            <label style={{fontSize:11,color:"#6b7280",marginLeft:10}}>Type</label>
            <select data-testid="inv-filter-kind" value={kind} onChange={e => setKind(e.target.value)} style={{...inp,width:230,marginTop:0}}>
              <option value="">All</option>
              <option value="supplier_to_platform">My invoices to GoPrintPack</option>
              <option value="platform_to_supplier">Platform fee invoices to me</option>
            </select>
          </>
        )}
        {role === "admin" && (
          <>
            <label style={{fontSize:11,color:"#6b7280",marginLeft:10}}>Type</label>
            <select data-testid="inv-filter-kind" value={kind} onChange={e => setKind(e.target.value)} style={{...inp,width:230,marginTop:0}}>
              <option value="">All</option>
              <option value="supplier_to_platform">Supplier → Platform</option>
              <option value="platform_to_customer">Platform → Customer</option>
              <option value="platform_to_supplier">Platform fee → Supplier</option>
            </select>
          </>
        )}
      </div>

      {data.invoices.length === 0 ? (
        <div className="pipeline-empty" data-testid="inv-empty">
          No invoices in this period yet.
        </div>
      ) : (
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",fontSize:12,borderCollapse:"collapse"}}>
            <thead>
              <tr style={{background:"#f3f4f6",textAlign:"left"}}>
                <th style={th}>Invoice #</th>
                <th style={th}>Date</th>
                <th style={th}>Job</th>
                <th style={th}>Other party</th>
                <th style={{...th,textAlign:"right"}}>Taxable</th>
                <th style={{...th,textAlign:"right"}}>GST</th>
                <th style={{...th,textAlign:"right"}}>Grand total</th>
                {isSupplier && <th style={{...th,textAlign:"right"}}>TDS</th>}
                <th style={th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {data.invoices.map(i => {
                const other = isAdmin ? (i.type === "supplier_to_platform" ? i.seller?.name : i.buyer?.name) : (i.type === "supplier_to_platform" ? i.buyer?.name : i.seller?.name);
                return (
                  <tr key={i.id} data-testid={`inv-row-${i.id}`} style={{borderBottom:"1px solid #e5e7eb"}}>
                    <td style={td}><b>{i.invoice_number}</b><div style={{fontSize:10,color:"#9ca3af"}}>{i.type.replace(/_/g," ")}</div></td>
                    <td style={td}>{i.invoice_date?.slice(0,10)}</td>
                    <td style={td} title={i.job_id}>{i.line_items?.[0]?.description?.split("—").slice(1).pop()?.trim() || i.job_id}</td>
                    <td style={td}>{other || "—"}</td>
                    <td style={{...td,textAlign:"right"}}>₹{i.subtotal_inr?.toLocaleString("en-IN")}</td>
                    <td style={{...td,textAlign:"right",color:"#065F46"}}>₹{i.gst_amount_inr?.toLocaleString("en-IN")}<div style={{fontSize:10,color:"#9ca3af"}}>@ {i.gst_pct}%</div>
                      {i.gst_pct_effective != null && <div data-testid={`inv-gst-corrected-${i.id}`} style={{fontSize:10,color:"#B45309"}}>Corrected → {i.gst_pct_effective}% ({i.gst_adjustment_inr < 0 ? "−" : "+"}₹{Math.abs(i.gst_adjustment_inr || 0).toLocaleString("en-IN")})</div>}
                    </td>
                    <td style={{...td,textAlign:"right",fontWeight:600}}>₹{i.grand_total_inr?.toLocaleString("en-IN")}</td>
                    {isSupplier && (
                      <td style={{...td,textAlign:"right",color:"#B45309"}}>{i.tds_amount_inr != null ? `−₹${i.tds_amount_inr?.toLocaleString("en-IN")}` : "—"}</td>
                    )}
                    <td style={td}>
                      <a data-testid={`inv-pdf-${i.id}`} href={`${API}/invoices/${i.id}/pdf`} target="_blank" rel="noopener noreferrer" className="primary-btn" style={{textDecoration:"none",fontSize:11,padding:"4px 12px"}}>Download</a>
                      {isAdmin && (data?.zoho_sync_enabled || i.zoho_status) && <ZohoBadge inv={i} onDone={load}/>}
                      {isAdmin && i.type !== "platform_to_supplier" && (
                        <button data-testid={`inv-gst-fix-${i.id}`} className="secondary-btn" onClick={() => setFixing(i)} style={{fontSize:11,padding:"4px 12px",marginLeft:6}}>Fix GST</button>
                      )}
                      {isAdmin && (i.platform_outdated ? (
                        <button data-testid={`inv-reissue-${i.id}`} className="secondary-btn" disabled={!!busy} onClick={() => reissue(i.id)} style={{fontSize:11,padding:"4px 12px",marginLeft:6}}>
                          {busy === i.id ? "…" : "Reissue"}
                        </button>
                      ) : i.reissued_at && (
                        <span data-testid={`inv-reissued-${i.id}`} style={{fontSize:10,color:"#065F46",marginLeft:6}}>Reissued {i.reissued_at.slice(0,10)}</span>
                      ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {isAdmin && <CreditNotesPanel refresh={cnRefresh}/>}
      {fixing && <GstFixModal invoice={fixing} close={() => setFixing(null)} onDone={() => { setFixing(null); setCnRefresh(r => r + 1); load(); }}/>}
    </section>
  );
}


// =========================================================================
export function SupplierSettlements({ supplierId }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");

  useEffect(() => {
    if (!supplierId) return;
    const url = `${API}/suppliers/${supplierId}/settlements${status ? `?status=${status}` : ""}`;
    axios.get(url).then(r => setData(r.data)).catch(() => setData({ settlements: [], totals: {} }));
  }, [supplierId, status]);

  if (!data) return <div className="content" data-testid="sup-settlements-loading">Loading…</div>;
  const t = data.totals || {};

  return (
    <section className="content" data-testid="sup-settlements">
      <div className="section-heading">
        <div>
          <p className="eyebrow">FINANCIAL SETTLEMENTS</p>
          <h2>Your payouts & TDS summary</h2>
          <p className="subhead">Every job we settle generates a sheet with your invoice value, GST, platform fee + 18% GST, TDS (194-O) and net wire. Download for your records.</p>
        </div>
      </div>

      <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:16}}>
        <StatPill label="Settlements" value={t.count || 0} testid="sup-stl-count"/>
        <StatPill label="Pending" value={t.pending_count || 0} testid="sup-stl-pending" tone="warn"/>
        <StatPill label="Paid" value={t.paid_count || 0} testid="sup-stl-paid" tone="ok"/>
        <StatPill label="Pending net" value={`₹${(t.pending_net_inr||0).toLocaleString("en-IN")}`} tone="warn" testid="sup-stl-pendingnet"/>
        <StatPill label="Lifetime paid" value={`₹${(t.paid_net_inr||0).toLocaleString("en-IN")}`} tone="ok" testid="sup-stl-lifepaid"/>
        <StatPill label="Lifetime TDS" value={`₹${(t.lifetime_tds_inr||0).toLocaleString("en-IN")}`} testid="sup-stl-lifetds"/>
      </div>

      <div style={{display:"flex",gap:6,marginBottom:10}}>
        {["", "pending", "paid"].map(s => (
          <button key={s || "all"} data-testid={`sup-stl-filter-${s || "all"}`} onClick={() => setStatus(s)}
            className={status === s ? "verify-pill ok" : "verify-pill pending"} style={{cursor:"pointer",textTransform:"capitalize"}}>
            {s || "all"}
          </button>
        ))}
      </div>

      {data.settlements.length === 0 ? (
        <div className="pipeline-empty" data-testid="sup-stl-empty">No settlements yet. Once a job dispatches and admin wires the payout, it'll show up here.</div>
      ) : (
        <div style={{display:"grid",gap:10}}>
          {data.settlements.map(s => (
            <article key={s.id} data-testid={`sup-stl-row-${s.id}`} style={{
              background: s.status === "paid" ? "#F0FDF4" : "#fff",
              border: `1px solid ${s.status === "paid" ? "#86EFAC" : "#e5e7eb"}`,
              borderRadius: 10, padding: 14,
            }}>
              <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:10}}>
                <div style={{flex:1,minWidth:260}}>
                  <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                    <b style={{fontSize:14}}>{s.settlement_number}</b>
                    <span className={`verify-pill ${s.status === "paid" ? "ok" : "pending"}`}>{s.status}</span>
                  </div>
                  <div style={{fontSize:12,color:"#6b7280",marginTop:4}}>{s.job_title} · {s.job_category}</div>
                  <div style={{fontSize:11,color:"#9ca3af"}}>Buyer: {s.buyer_name || "—"} · Created {s.created_at?.slice(0,10)}</div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:11,color:"#6b7280"}}>Net wire</div>
                  <div style={{fontSize:22,fontWeight:700,color:"#065F46"}} data-testid={`sup-stl-net-${s.id}`}>₹{s.net_payable_inr?.toLocaleString("en-IN")}</div>
                </div>
              </div>

              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))",gap:10,marginTop:12,fontSize:11,color:"#374151",background:"#F9FAFB",padding:10,borderRadius:8}}>
                <div><div style={{color:"#9ca3af"}}>Gross</div><b>₹{s.gross_inr?.toLocaleString("en-IN")}</b></div>
                <div><div style={{color:"#9ca3af"}}>GST ({s.gst_rate_pct}%)</div><b>₹{s.gst_amount_inr?.toLocaleString("en-IN")}</b></div>
                <div><div style={{color:"#9ca3af"}}>Supplier invoice</div><b>₹{s.supplier_gross_inr?.toLocaleString("en-IN")}</b></div>
                <div><div style={{color:"#9ca3af"}}>Platform fee</div><b style={{color:"#B91C1C"}}>−₹{s.platform_commission_inr?.toLocaleString("en-IN")}</b></div>
                {s.platform_fee_gst_inr != null && <div data-testid={`sup-stl-feegst-${s.id}`}><div style={{color:"#9ca3af"}}>18% GST on fee (ITC)</div><b style={{color:"#B91C1C"}}>−₹{s.platform_fee_gst_inr?.toLocaleString("en-IN")}</b></div>}
                <div><div style={{color:"#9ca3af"}}>TDS ({s.tds_rate_pct}%)</div><b style={{color:"#B45309"}}>−₹{s.tds_amount_inr?.toLocaleString("en-IN")}</b></div>
              </div>

              {s.status === "paid" && (
                <div style={{marginTop:10,fontSize:11,color:"#065F46",background:"#DCFCE7",padding:10,borderRadius:8}}>
                  <b>Paid</b> · UTR <code>{s.utr}</code> · {s.method?.toUpperCase()} · {s.paid_at?.slice(0,10)}
                </div>
              )}

              <div style={{marginTop:10,display:"flex",gap:8,justifyContent:"flex-end"}}>
                <a data-testid={`sup-stl-pdf-${s.id}`} href={`${API}/settlements/${s.id}/pdf`} target="_blank" rel="noopener noreferrer" className="primary-btn" style={{textDecoration:"none",fontSize:12,padding:"6px 14px"}}>
                  Download settlement sheet
                </a>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
